import _ from 'underscore';
import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Button, Glyphicon } from '@bingads-webui-react/primitive';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { downloadifyBlob } from '@bingads-webui/download-service';

import { getConfig } from '../repository/config';
import { odata } from '../repository/odata';
import { getWordpressInstructionsStyles } from '../styles/wordpress-instructions-styles';

import imageStep2 from '../../media/wordpress-step2.png';
import imageStep3 from '../../media/wordpress-step3.png';
import imageStep4 from '../../media/wordpress-step4.png';

const WordpressInstructionsUnstyled = (props) => {
  const {
    classes,
    tagData,
    logTrace,
    serverContext,
  } = props;
  const i18n = useContext(I18nContext);
  const noTranslateModel = { noTranslate: text => (<b>{text}</b>) };
  const step2Bulletin1Template = i18n.getString(_TL_('1. Go to your {{noTranslate}}Dashboard{{/noTranslate}}.'));
  const step2Bulletin2Template = i18n.getString(_TL_('2. Click {{noTranslate}}Plugins{{/noTranslate}} and select {{noTranslate}}Add New{{/noTranslate}}.'));
  const step3Bulletin1Template = i18n.getString(_TL_('1. Click {{noTranslate}}Upload Plugin{{/noTranslate}}.'));
  const step3Bulletin2Template = i18n.getString(_TL_('2. Click {{noTranslate}}Choose file{{/noTranslate}} and select {{noTranslate}}Microsoft-Advertising-UET-TagID.zip{{/noTranslate}}.'));
  const step3Bulletin3Template = i18n.getString(_TL_('3. Click {{noTranslate}}Install Now{{/noTranslate}}.'));
  const step4Bulletin1Template = i18n.getString(_TL_('1. Click {{noTranslate}}Plugins{{/noTranslate}}.'));
  const step4Bulletin2Template = i18n.getString(_TL_('2. Look for {{noTranslate}}Microsoft Advertising Universal Event Tracking (UET){{/noTranslate}} under installed plugins.'));
  const step4Bulletin3Template = i18n.getString(_TL_('3. Click {{noTranslate}}Activate{{/noTranslate}}.'));

  const downloadGenericError = i18n.getString(_TL_('We\'ve encountered an error while downloading this plugin. Please refresh the page and try again.'));

  const [odataInstance, setOdataInstance] = useState(null);
  const odataConfig = getConfig({});

  useEffect(() => {
    if (!odataInstance) {
      if (!odataConfig.has('odata.path')) {
        const {
          CurrentLocale,
          CultureLcid,
          ServicesEndpoints,
          CampaignAPIPath,
          Token,
          CurrentAccount,
          CurrentCustomer,
          CurrentUser,
        } = serverContext;
        odataConfig.set({
          CultureName: CurrentLocale,
          CultureLcid,
          'odata.url': ServicesEndpoints && ServicesEndpoints.CampaignAPI, // https://ui.ads-int.microsoft.com/ODataApi
          'odata.path': CampaignAPIPath || '/Campaign/V2', // /Campaign/V2
          'odata.token': Token,
          CurrentUser,
          CurrentCustomer,
          CurrentAccount,
          AccountTimeZone: (CurrentAccount && CurrentAccount.TimeZone) || 71,
        });
      }
      setOdataInstance(odata(odataConfig, {}));
    }
  }, [odataConfig, odataInstance, serverContext]);

  const doDownload = () => {
    logTrace('[WordPress] download clicked');

    const getTagPuglinBlobUrl = `/Customers(${tagData.cid})/Accounts(${tagData.aid})/Tags(${tagData.tagId})/Default.GetTagPuglinBlobUrl()`;
    odataInstance.get(getTagPuglinBlobUrl)
      .then((response) => {
        const downloadUrl = response.value;
        fetch(downloadUrl)
          .then(res => res.blob())
          .then((blob) => {
            const originalType = blob.type.split('/').pop();
            // do not download xml error file
            if (originalType === 'zip') {
              const fileNameExtract = downloadUrl.match(/Microsoft(.)*\.zip/);
              downloadifyBlob(blob, fileNameExtract[0]);
            } else {
              notificationApi.showWarning(downloadGenericError);
            }
          });
      })
      .catch((response) => {
        if (response.body && response.body.Code) {
          if (_.some(response.body.value, v => v.Code === 'InvalidCustomerId')) {
            notificationApi.showWarning(i18n.getString(_TL_('The manager account ID does not exist.')));
          }
        } else {
          notificationApi.showWarning(downloadGenericError);
        }

        logTrace('[WordPress] download error');
      });
  };

  return (
    <div className={classNames(classes.container, 'insert-instruction')}>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>{i18n.getString(_TL_('Step 1: Download the Microsoft Advertising UET plugin.'))}</p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <p>{i18n.getString(_TL_('Click'))} <b>{i18n.getString(_TL_('Download'))}</b> {i18n.getString(_TL_('for a .zip file that you’ll install in your WordPress admin panel.'))}</p>
          <div className={classNames(classes.instructionInfo)}><Glyphicon className={classNames(classes.instructionInfoIcon)} glyph="ba-Info" />{i18n.getString(_TL_('This integration is supported for'))} <b>{i18n.getString(_TL_('WordPress versions 4.4'))}</b> {i18n.getString(_TL_('and above.'))}</div>
          <div className={classNames(classes.downloadButton)}><Button onClick={doDownload}><Glyphicon className={classNames(classes.downloadButtonIcon)} glyph="ba-Download" />{i18n.getString(_TL_('Download'))}</Button></div>
        </div>
      </div>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>{i18n.getString(_TL_('Step 2: Add new plugin in your WordPress account.'))}</p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <img className={classNames(classes.instructionImage)} src={imageStep2} alt={i18n.getString(_TL_('WordPress admin page'))} />
          <p>{i18n.getString(_TL_('Make sure you open a new tab and are logged into your WordPress account.'))}</p>
          <p>
            <ReactTemplate
              model={noTranslateModel}
              template={step2Bulletin1Template}
            />
          </p>
          <p>
            <ReactTemplate
              model={noTranslateModel}
              template={step2Bulletin2Template}
            />
          </p>
        </div>
      </div>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>{i18n.getString(_TL_('Step 3: Install the plugin.'))}</p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <img className={classNames(classes.instructionImage)} src={imageStep3} alt={i18n.getString(_TL_('WordPress admin plugin upload page'))} />
          <p>{i18n.getString(_TL_('Make sure you open a new tab and are logged into your WordPress account.'))}</p>
          <p>
            <ReactTemplate
              model={noTranslateModel}
              template={step3Bulletin1Template}
            />
          </p>
          <p>
            <ReactTemplate
              model={noTranslateModel}
              template={step3Bulletin2Template}
            />
          </p>
          <p>
            <ReactTemplate
              model={noTranslateModel}
              template={step3Bulletin3Template}
            />
          </p>
        </div>
      </div>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>{i18n.getString(_TL_('Step 4: Activate the plugin'))}.</p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <img className={classNames(classes.instructionImage)} src={imageStep4} alt={i18n.getString(_TL_('WordPress admin installed plugins page'))} />
          <p>
            <ReactTemplate
              model={noTranslateModel}
              template={step4Bulletin1Template}
            />
          </p>
          <p>
            <ReactTemplate
              model={noTranslateModel}
              template={step4Bulletin2Template}
            />
          </p>
          <p>
            <ReactTemplate
              model={noTranslateModel}
              template={step4Bulletin3Template}
            />
          </p>
        </div>
      </div>
    </div>
  );
};

WordpressInstructionsUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getWordpressInstructionsStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  tagData: PropTypes.shape({
    cid: PropTypes.number.isRequired,
    aid: PropTypes.number.isRequired,
    tagId: PropTypes.number.isRequired,
  }).isRequired,
  logTrace: PropTypes.func.isRequired,
  serverContext: PropTypes.func.isRequired,
};

export const WordpressInstructions = withDefaultStyles(WordpressInstructionsUnstyled, getWordpressInstructionsStyles);

